"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Mailbox, Save as SaveIcon } from "lucide-react";
import { WorkspaceSectionHeader } from "../workspace-section-header";
import { ResendDomainSetup } from "./resend-domain-setup";
import { PlatformEmailDefaultBanner } from "./platform-email-default-banner";
import { useBrandById } from "@/hooks/use-brands";
import {
  hasCustomBookingEmailDomain,
  PLATFORM_BOOKING_EMAIL_DOMAIN,
  resolveBookingEmailDisplay,
} from "@/@sdk/brands/platform-email-config.helpers";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSDKActionWithDeps } from "@/@sdk/sdk.hooks";
import { toast } from "sonner";

function parseAlertEmails(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((v) => v.trim())
    .filter(Boolean);
}

export function BookingEmailConfigSection({ brandId }: { brandId: string }) {
  const brand = useBrandById(brandId);
  const usesPlatformBookingEmail = !hasCustomBookingEmailDomain(brand);

  const [alertEmailsInput, setAlertEmailsInput] = useState("");

  useEffect(() => {
    setAlertEmailsInput(
      (brand?.bookingConfirmationAlertEmails ?? []).join(", ")
    );
  }, [brand?.bookingConfirmationAlertEmails, brandId]);

  const platformFromDisplay = useMemo(() => {
    const resolved = resolveBookingEmailDisplay(brand, "preview-booking-id");
    if (!resolved.isReady || resolved.source !== "platform") {
      return `bookings@${PLATFORM_BOOKING_EMAIL_DOMAIN}`;
    }
    const name = brand?.name?.trim() || "Bookings";
    return `${name} <${resolved.fromEmail}>`;
  }, [brand]);

  const { doAction: editBrand, isProcessing } = useSDKActionWithDeps(
    () => ({
      action:
        (SDK) =>
        (edits: { bookingConfirmationAlertEmails: string[] }) =>
          SDK.brands.editById(brandId, edits),
      failureMessage: "Failed to update booking confirmation alert emails",
    }),
    [brandId]
  );

  const handleSaveAlertEmails = useCallback(async () => {
    const emails = parseAlertEmails(alertEmailsInput);
    try {
      await editBrand({ bookingConfirmationAlertEmails: emails });
      toast.success("Booking confirmation alert emails updated");
    } catch (error) {
      toast.error("Failed to update booking confirmation alert emails");
      console.error(error);
    }
  }, [alertEmailsInput, editBrand]);

  return (
    <div className="space-y-6 px-4 pb-6">
      <WorkspaceSectionHeader
        icon={Mailbox}
        title="Booking Email Config"
        description="Booking emails work out of the box with the TA Desk default domain. Add your own domain below to send from bookings@yourdomain and route replies to your brand."
      />

      {usesPlatformBookingEmail && (
        <PlatformEmailDefaultBanner
          variant="booking"
          fromAddress={platformFromDisplay}
        />
      )}

      <div className="rounded-md border p-4 space-y-3">
        <div className="space-y-1">
          <Label htmlFor="booking-confirmation-alert-emails">
            Booking Confirmation Alert Emails
          </Label>
          <p className="text-xs text-muted-foreground">
            These addresses are automatically CC&apos;d whenever a booking
            confirmation email is sent for this brand.
          </p>
        </div>
        <Input
          id="booking-confirmation-alert-emails"
          value={alertEmailsInput}
          onChange={(e) => setAlertEmailsInput(e.target.value)}
          placeholder="ops@example.com, alerts@example.com"
        />
        <div className="flex justify-end">
          <Button
            type="button"
            size="sm"
            onClick={handleSaveAlertEmails}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              <>
                <SaveIcon className="mr-2 h-4 w-4" />
                Save alert emails
              </>
            )}
          </Button>
        </div>
      </div>

      <ResendDomainSetup brandId={brandId} purpose="booking" />
    </div>
  );
}
